import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { getToken, clearAuth } from './authStorage';
import { MONTHS } from './constants';
import './App.css';
import Challenge from './Challenge'; // Assuming this is your Login component
import ProtectedApp from './ProtectedApp';

// Auto-detect unified deployment: if served from the same origin as the API, use relative URLs
const isUnifiedDeployment = window.location.hostname !== 'localhost' || window.location.port !== '3000';
const API_URL = isUnifiedDeployment ? '' : (process.env.REACT_APP_API_URL || 'https://budget-app-94r3.onrender.com');

console.log('API URL:', API_URL || '(relative — unified deployment)', 'Host:', window.location.hostname);

axios.interceptors.request.use(config => {
  // Add API URL prefix to relative URLs if not in unified deployment
  if (config.url && !config.url.startsWith('http') && API_URL) {
    config.url = `${API_URL}${config.url.startsWith('/') ? '' : '/'}${config.url}`;
  }
  
  // Add auth token if available
  const token = getToken();
  if (token) {
    config.headers.Authorization = token;
  }
  
  return config;
});

// Expired/invalid token on any call → clear it and go back to the login screen
axios.interceptors.response.use(
  response => response,
  error => {
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.includes('/api/verify-challenge') && getToken()) {
      clearAuth();
      window.location.assign('/');
    }
    return Promise.reject(error);
  }
);

function App() {
  const [budgetData, setBudgetData] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [error, setError] = useState('');
  const [isLoadingBudgets, setIsLoadingBudgets] = useState(false);
  const [authenticated, setAuthenticated] = useState(null); // null = checking
  const navigate = useNavigate();

  const fetchBudgets = useCallback(async () => {
    setIsLoadingBudgets(true);
    try {
      const response = await axios.get('/api/budgets', { timeout: 10000, withCredentials: true });
      console.log('Fetched budgets:', response.data);
      setBudgetData(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching budgets:', err.message);
      setError(`Failed to load budgets: ${err.message}`);
      setBudgetData([]);
    } finally {
      setIsLoadingBudgets(false);
    }
  }, []);

  const refreshData = useCallback((month) => {
    console.log('refreshData triggered with:', month);
    const now = new Date();
    const currentMonth = `${MONTHS[now.getMonth()]} ${now.getFullYear()}`;
    if (!month || month === currentMonth) {
      console.log('Refreshing budgets for current month:', currentMonth);
      fetchBudgets();
    } else {
      console.log('Skipping budgets fetch for historical month:', month);
    }
  }, [fetchBudgets]);

  const handleSubmitSuccess = useCallback(() => {
    console.log('handleSubmitSuccess triggered');
    fetchBudgets();
  }, [fetchBudgets]);

  // navigate changes identity on every route change — keep it in a ref so the auth check runs once
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const handleLogin = useCallback((user) => {
    setAuthenticated(true);
    fetchBudgets();
    navigateRef.current(`/${user}`, { replace: true });
  }, [fetchBudgets]);

  const handleLogout = useCallback(() => {
    clearAuth();
    setAuthenticated(false);
    setBudgetData([]);
    setSummaryData({});
    navigateRef.current('/', { replace: true });
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      console.log('isAuthenticated check — token present:', !!token, 'Path:', window.location.pathname);
      if (!token) {
        setAuthenticated(false);
        return;
      }
      try {
        // Cheap token check — no Google Sheets call
        await axios.get('/api/me', { withCredentials: true });
      } catch (err) {
        if (err.response?.status === 401) {
          clearAuth();
          setAuthenticated(false);
          navigateRef.current('/', { replace: true });
          return;
        }
        // Network/server hiccup: the token is probably fine — don't log the user out
        console.error('Auth check failed (not a 401), staying logged in:', err.message);
      }
      setAuthenticated(true);
      fetchBudgets();
      const user = localStorage.getItem('budget_user') === 'yehudit' ? 'yehudit' : 'daniel';
      if (window.location.pathname === '/') navigateRef.current(`/${user}`, { replace: true });
    };
    checkAuth();
  }, [fetchBudgets]);

  if (authenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  console.log('App rendered with summaryData:', summaryData);

  return (
    <>
      {error && <div className="bg-red-100 text-red-700 p-4 text-center">{error}</div>}
      {isLoadingBudgets && <div className="bg-blue-100 text-blue-700 p-4 text-center">Loading budgets...</div>}
      <Routes>
        <Route path="/" element={<Challenge onLogin={handleLogin} />} />
        <Route
          path="/*"
          element={
            authenticated ? (
              <ProtectedApp
                budgetData={budgetData}
                setBudgetData={setBudgetData}
                summaryData={summaryData}
                setSummaryData={setSummaryData}
                refreshData={refreshData}
                fetchBudgets={fetchBudgets}
                handleSubmitSuccess={handleSubmitSuccess}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="*" element={<div>404 - Page Not Found</div>} />
      </Routes>
    </>
  );
}

export default function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}